import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export default function PushBell({ compact = false }) {
  const { user } = useAuth();
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return;
    setSupported(true);
    setPermission(Notification.permission);
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch { /* ignore */ }
    })();
  }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notifications blocked — enable them in your browser settings.");
        return;
      }
      const { data } = await api.get("/notifications/vapid-public-key");
      if (!data.publicKey) {
        toast.error("Push server not configured");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      const raw = sub.toJSON();
      await api.post("/notifications/subscribe", {
        endpoint: raw.endpoint,
        keys: raw.keys,
      });
      setSubscribed(true);
      const who = user?.role === "brand" ? "creator" : "brand";
      toast.success(`Push notifications enabled — you'll get pinged the moment a ${who} accepts.`);
    } catch (e) {
      toast.error("Couldn't enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const raw = sub.toJSON();
        await api.post("/notifications/unsubscribe", { endpoint: raw.endpoint, keys: raw.keys || {} });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Push notifications disabled");
    } catch {
      toast.error("Couldn't disable notifications");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      await api.post("/notifications/test");
      toast.success("Test push sent — check your device");
    } catch {
      toast.error("Test push failed");
    }
  };

  if (!supported) return null;

  if (compact) {
    return (
      <button
        onClick={subscribed ? unsubscribe : subscribe}
        disabled={busy}
        data-testid="push-bell-compact"
        title={subscribed ? "Notifications on" : "Enable notifications"}
        className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${
          subscribed
            ? "nexus-gradient text-white"
            : "bg-white/5 border border-white/15 text-zinc-300 hover:border-white/25"
        }`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : subscribed ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <div className="glass rounded-2xl p-5" data-testid="push-panel">
      <div className="flex items-start gap-4">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${
          subscribed ? "nexus-gradient" : "bg-white/[0.03] hairline"
        }`}>
          {subscribed ? <BellRing className="h-5 w-5 text-white" /> : <Bell className="h-5 w-5 text-zinc-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Push notifications</div>
          <div className="font-semibold text-white">
            {subscribed ? "You'll be pinged instantly" : `Get pinged when a ${user?.role === "brand" ? "creator" : "brand"} accepts`}
          </div>
          <p className="text-xs text-zinc-400 mt-1">
            {subscribed
              ? "Application accepted, escrow funded, delivery approved, or a new message — all live to your device."
              : "Enable browser push so you never miss an application accept, payment release or new message."}
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {subscribed ? (
              <>
                <button
                  onClick={sendTest}
                  disabled={busy}
                  className="bg-white/5 border border-white/15 text-white text-xs font-semibold px-3 h-8 rounded-full hover:bg-white/10"
                  data-testid="push-test-btn"
                >
                  Send test push
                </button>
                <button
                  onClick={unsubscribe}
                  disabled={busy}
                  className="text-xs text-zinc-400 hover:text-nexus-pink px-3 h-8 rounded-full inline-flex items-center gap-1"
                  data-testid="push-disable-btn"
                >
                  <BellOff className="h-3 w-3" /> Turn off
                </button>
              </>
            ) : (
              <button
                onClick={subscribe}
                disabled={busy || permission === "denied"}
                className="nexus-gradient text-white text-xs font-semibold px-4 h-8 rounded-full hover:opacity-90 inline-flex items-center gap-1"
                data-testid="push-enable-btn"
              >
                {busy && <Loader2 className="h-3 w-3 animate-spin" />}
                {permission === "denied" ? "Blocked in browser" : "Enable notifications"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
